import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, FileText, Link2, Mail } from 'react-feather';
import { EtlSection, EtlGrid, EtlField, ChipInput, ChoiceCards, EtlBanner, EtlKv } from '../EtlPages';

// ─── Main component ───────────────────────────────────────────────────────────

const SUPPORTED_EXTENSIONS = ['.pdf', '.xlsx', '.xls', '.xlsm', '.csv', '.txt', '.xml', '.json'];

// Link-in-email providers (mirrors azfEtl/services/email_link_download.py).
// Some vendors email a link instead of an attachment; the ETL follows the link,
// materialises the file, and routes it through the normal fileConfig matcher.
const LINK_PROVIDERS = [
  {
    value: 'cloudbeds_insights',
    label: 'Cloudbeds Insights',
    description: '"Download Report" button → datainsights.cloudbeds.com/?token=… The ETL resolves the token and downloads the report file (7-day link).',
  },
  {
    value: 'bwh_rmws',
    label: 'BWH RM Workspace (shared report)',
    description: 'rmws.bwhhotelgroup.com/shared_report/… The ETL pulls the report data and produces the same "Detail Table" CSV as the page\'s Download CSV button.',
  },
  {
    value: 'direct',
    label: 'Direct file link',
    description: 'The link itself is the file (…/report.xlsx, …/export.csv). Use the URL pattern to pick the right link if the email has several.',
  },
];

const normalizeLinkDownload = (ld) => {
  if (!ld || typeof ld !== 'object') return { enabled: false, provider: 'cloudbeds_insights', urlPattern: '', filename: '' };
  return {
    enabled: !!ld.enabled,
    provider: LINK_PROVIDERS.some(p => p.value === ld.provider) ? ld.provider : 'cloudbeds_insights',
    urlPattern: ld.urlPattern || '',
    filename: ld.filename || '',
  };
};


const EmailIntegrationConfig = ({ onEmailConfigChange, initialConfig = {} }) => {
  const [emailConfig, setEmailConfig] = useState({
    emailMatchType: 'file',
    auditFile: '',
    keywords: [],
    matchMode: 'contains',
    allowedExtensions: [],
    excludeKeywords: [],
    fileType: 'audit',
    linkDownload: normalizeLinkDownload(null),
    ...initialConfig,
    linkDownload: normalizeLinkDownload(initialConfig.linkDownload),
    // Ensure arrays on load (old configs may have undefined)
    keywords: Array.isArray(initialConfig.keywords) ? initialConfig.keywords : [],
    allowedExtensions: Array.isArray(initialConfig.allowedExtensions)
      ? initialConfig.allowedExtensions.filter(e => SUPPORTED_EXTENSIONS.includes(e))
      : [],
    excludeKeywords: Array.isArray(initialConfig.excludeKeywords) ? initialConfig.excludeKeywords : [],
  });

  const [validationResult, setValidationResult] = useState(null);
  const [previewExamples, setPreviewExamples] = useState([]);

  // Resync local state when the parent loads a saved schema AFTER mount
  // (existing-schema recall). Without this the keyword/match fields keep
  // their stale mount-time values, and the effect below then pushes those
  // stale values back up — clobbering the freshly recalled config.
  useEffect(() => {
    if (!initialConfig || Object.keys(initialConfig).length === 0) return;
    const normalized = {
      emailMatchType: initialConfig.emailMatchType || 'file',
      auditFile: initialConfig.auditFile || '',
      matchMode: initialConfig.matchMode || 'contains',
      fileType: initialConfig.fileType || 'audit',
      customFileType: initialConfig.customFileType,
      keywords: Array.isArray(initialConfig.keywords) ? initialConfig.keywords : [],
      allowedExtensions: Array.isArray(initialConfig.allowedExtensions)
        ? initialConfig.allowedExtensions.filter(e => SUPPORTED_EXTENSIONS.includes(e))
        : [],
      excludeKeywords: Array.isArray(initialConfig.excludeKeywords) ? initialConfig.excludeKeywords : [],
      linkDownload: normalizeLinkDownload(initialConfig.linkDownload),
    };
    setEmailConfig(prev => {
      const merged = { ...prev, ...normalized };
      // Identity-preserving compare so an echo of our own pushed-up state
      // doesn't loop: only adopt the incoming config when it differs.
      return JSON.stringify(merged) === JSON.stringify(prev) ? prev : merged;
    });
  }, [initialConfig]);

  useEffect(() => {
    onEmailConfigChange(emailConfig);
    generatePreviewExamples();
  }, [emailConfig]);

  const updateConfig = (updates) => {
    setEmailConfig(prev => ({ ...prev, ...updates }));
  };

  // All keywords (primary + OR array) for matching preview
  const allKeywords = [
    ...(emailConfig.auditFile.trim() ? [emailConfig.auditFile.trim()] : []),
    ...emailConfig.keywords,
  ];

  const matchesTarget = (target) => {
    if (allKeywords.length === 0) return false;
    const t = target.toLowerCase();
    const positiveMatch = emailConfig.matchMode === 'exact'
      ? allKeywords.some(kw => t === kw.toLowerCase())
      : allKeywords.some(kw => t.includes(kw.toLowerCase()));
    if (!positiveMatch) return false;
    // Exclude keywords always use contains mode against the full target
    if (emailConfig.excludeKeywords.length > 0) {
      const excluded = emailConfig.excludeKeywords.some(kw => t.includes(kw.toLowerCase()));
      if (excluded) return false;
    }
    return true;
  };

  const generatePreviewExamples = () => {
    if (allKeywords.length === 0) {
      setPreviewExamples([]);
      return;
    }

    const kw = emailConfig.auditFile || emailConfig.keywords[0] || '';

    if (emailConfig.emailMatchType === 'file') {
      const examples = emailConfig.matchMode === 'exact'
        ? [
            { text: `${kw}.pdf`, matches: true },
            { text: `${kw.toUpperCase()}.xlsx`, matches: true },
            { text: `${kw} Summary.pdf`, matches: false },
            { text: `Daily ${kw} Report.pdf`, matches: false },
            { text: 'Monthly_Summary_Report.pdf', matches: false },
          ]
        : [
            { text: `Hotel_${kw}_Report_20250122.pdf`, matches: true },
            { text: `${kw.toUpperCase()}_Summary.xlsx`, matches: true },
            { text: `Financial_Report_${kw}_Jan.pdf`, matches: true },
            { text: 'Monthly_Summary_Report.pdf', matches: false },
            { text: 'Revenue_Analysis.xlsx', matches: false },
          ];

      // Re-evaluate matches using actual logic
      const stem = (name) => name.replace(/\.[^.]+$/, '');
      setPreviewExamples(examples.map(ex => ({
        ...ex,
        matches: matchesTarget(emailConfig.matchMode === 'exact' ? stem(ex.text) : ex.text),
      })));
    } else {
      const examples = emailConfig.matchMode === 'exact'
        ? [
            { text: `${kw}`, matches: true },
            { text: `${kw} Report`, matches: false },
            { text: `RE: ${kw} Summary`, matches: false },
            { text: 'Monthly Financial Summary', matches: false },
            { text: 'Weekly Revenue Report', matches: false },
          ]
        : [
            { text: `${kw} Report - Hotel ABC`, matches: true },
            { text: `Automated ${kw.toUpperCase()} Summary`, matches: true },
            { text: `RE: ${kw} Numbers for Today`, matches: true },
            { text: 'Monthly Financial Summary', matches: false },
            { text: 'Weekly Revenue Report', matches: false },
          ];

      setPreviewExamples(examples.map(ex => ({
        ...ex,
        matches: matchesTarget(ex.text),
      })));
    }
  };

  const validateConfiguration = () => {
    const errors = [];
    const warnings = [];

    if (allKeywords.length === 0) {
      errors.push('At least one keyword is required (primary keyword or an OR keyword)');
    } else {
      allKeywords.forEach(kw => {
        if (kw.length < 2) warnings.push(`Keyword "${kw}" is very short and may match too many files`);
      });
    }

    if (!emailConfig.fileType.trim()) {
      errors.push('File type is required');
    }

    const result = { isValid: errors.length === 0, errors, warnings };
    setValidationResult(result);
    return result;
  };

  const fileTypeOptions = [
    { value: 'audit', label: 'Audit Reports', description: 'Daily audit and financial reports' },
    { value: 'occupancy', label: 'Occupancy Reports', description: 'Room occupancy and forecasting data' },
    { value: 'revenue', label: 'Revenue Reports', description: 'Revenue analysis and breakdowns' },
    { value: 'market', label: 'Market Reports', description: 'Market segment revenue and room data' },
    { value: 'historical', label: 'Historical Reports', description: 'Historical revenue and room stats data extraction from PDFs' },
    { value: 'journal', label: 'Journal Reports', description: 'Daily journal totals for revenue, tax, and payments' },
    { value: 'custom', label: 'Custom', description: 'Custom file type' },
  ];

  return (
    <>
      {/* File type first: it decides which downstream steps exist and, when it
          changes, the wizard drops the work derived from the old one. Asking
          for it last meant filling in every keyword and then tripping a reset. */}
      <EtlSection
        title="What kind of file is this?"
        description="Determines which normalizer reads the file and which steps follow."
      >
        <EtlGrid>
          <EtlField label="File type" required help={fileTypeOptions.find((o) => o.value === emailConfig.fileType)?.description}>
            <select
              className="cp-input"
              value={emailConfig.fileType}
              onChange={(e) => updateConfig({ fileType: e.target.value })}
            >
              {fileTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </EtlField>
          {emailConfig.fileType === 'custom' ? (
            <EtlField label="Custom type name" required>
              <input
                type="text"
                className="cp-input"
                value={emailConfig.customFileType || ''}
                onChange={(e) => updateConfig({ customFileType: e.target.value })}
                placeholder="e.g. taxes"
              />
            </EtlField>
          ) : null}
        </EtlGrid>
      </EtlSection>

      <EtlSection
        title="Where the keyword is checked"
        description="Filenames are the reliable signal for most hotels; use the subject when the attachment name changes daily."
      >
        <ChoiceCards
          ariaLabel="Match target"
          value={emailConfig.emailMatchType}
          onChange={(v) => updateConfig({ emailMatchType: v })}
          options={[
            { value: 'file', title: 'Attachment filename', icon: FileText, sub: 'Match against the name of the attached file.' },
            { value: 'subject', title: 'Email subject', icon: Mail, sub: 'Match against the subject line instead.' },
          ]}
        />
      </EtlSection>

      <EtlSection title="Keywords">
        <EtlGrid single>
          <EtlField
            label={emailConfig.emailMatchType === 'file' ? 'Primary filename keyword' : 'Primary subject keyword'}
            required
            help={emailConfig.emailMatchType === 'file'
              ? 'The keyword that appears in the attachment filename.'
              : 'The keyword that appears in the email subject line.'}
          >
            <input
              type="text"
              className="cp-input"
              value={emailConfig.auditFile}
              onChange={(e) => updateConfig({ auditFile: e.target.value })}
              onBlur={validateConfiguration}
              placeholder={emailConfig.emailMatchType === 'file' ? 'Daily, Final, Audit…' : 'Daily Report, Audit Summary…'}
            />
          </EtlField>

          <EtlField
            label="Alternate keywords"
            help="Accepted if the primary keyword OR any of these match — for a report that arrives under different names on different days."
          >
            <ChipInput
              value={emailConfig.keywords}
              onChange={(next) => updateConfig({ keywords: next })}
              placeholder="summary, sales…"
              ariaLabel="Alternate keywords"
            />
          </EtlField>

          <EtlField
            label="Exclude keywords"
            help={'Rejects a file that matched a keyword but also contains one of these — "Revenue" + exclude "Summary" accepts "Revenue Detail.pdf" and rejects "Revenue Detail Summary.pdf".'}
          >
            <ChipInput
              exclude
              value={emailConfig.excludeKeywords}
              onChange={(next) => updateConfig({ excludeKeywords: next.map((k) => k.trim().toLowerCase()) })}
              placeholder="summary, detail…"
              ariaLabel="Exclude keywords"
            />
          </EtlField>
        </EtlGrid>
      </EtlSection>

      <EtlSection title="How strictly to match">
        <ChoiceCards
          ariaLabel="Match mode"
          value={emailConfig.matchMode}
          onChange={(v) => updateConfig({ matchMode: v })}
          options={[
            {
              value: 'contains',
              title: 'Contains',
              sub: `The keyword appears anywhere in the ${emailConfig.emailMatchType === 'file' ? 'filename' : 'subject'}. The default, and never loses a file.`,
            },
            {
              value: 'exact',
              title: 'Exact',
              sub: emailConfig.emailMatchType === 'file'
                ? 'The filename stem must equal the keyword — "Sales" will not match "Sales Summary".'
                : 'The subject must equal the keyword exactly. Use when a short keyword over-matches.',
            },
          ]}
        />
      </EtlSection>

      <EtlSection
        title="Accepted formats"
        description="Leave everything off to accept any supported format. Restrict it when two formats of the same report arrive and only one should be read."
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {SUPPORTED_EXTENSIONS.map((ext) => {
            const active = emailConfig.allowedExtensions.includes(ext);
            return (
              <button
                key={ext}
                type="button"
                aria-pressed={active}
                className={`cp-chip cp-mono${active ? ' cp-active' : ''}`}
                onClick={() => updateConfig({
                  allowedExtensions: active
                    ? emailConfig.allowedExtensions.filter((e) => e !== ext)
                    : [...emailConfig.allowedExtensions, ext],
                })}
              >
                {ext}
              </button>
            );
          })}
        </div>
        {emailConfig.allowedExtensions.length > 0 ? (
          <div className="cp-help" style={{ marginTop: 8 }}>
            Only {emailConfig.allowedExtensions.join(', ')} will be accepted for this feed.
          </div>
        ) : null}
      </EtlSection>

      <EtlSection title="Where the file comes from">
        <ChoiceCards
          ariaLabel="File source"
          value={emailConfig.linkDownload.enabled ? 'link' : 'attachment'}
          onChange={(v) => updateConfig({ linkDownload: { ...emailConfig.linkDownload, enabled: v === 'link' } })}
          options={[
            { value: 'attachment', title: 'Email attachment', icon: FileText, sub: 'The file arrives attached to the email.' },
            { value: 'link', title: 'Link in the email body', icon: Link2, sub: 'No attachment — a download link that changes daily. The ETL follows it and fetches the file.' },
          ]}
        />

        {emailConfig.linkDownload.enabled ? (
          <div className="ep-panel" style={{ marginTop: 12 }}>
            <EtlGrid single>
              <EtlField
                label="Link provider"
                help={LINK_PROVIDERS.find((p) => p.value === emailConfig.linkDownload.provider)?.description}
              >
                <select
                  className="cp-input"
                  value={emailConfig.linkDownload.provider}
                  onChange={(e) => updateConfig({ linkDownload: { ...emailConfig.linkDownload, provider: e.target.value } })}
                >
                  {LINK_PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </EtlField>
              <EtlField
                label="Link URL pattern"
                help="Regex. Only needed when the email carries several links and the provider default would pick the wrong one."
              >
                <input
                  type="text"
                  className="cp-input cp-mono"
                  value={emailConfig.linkDownload.urlPattern}
                  onChange={(e) => updateConfig({ linkDownload: { ...emailConfig.linkDownload, urlPattern: e.target.value } })}
                  placeholder={emailConfig.linkDownload.provider === 'direct' ? '\\.xlsx(\\?|$)   or   /exports/daily' : 'Leave empty — the provider knows its own link shape'}
                />
              </EtlField>
              <EtlField
                label="Saved filename"
                help="Placeholders: {original} (the vendor's name), {date} (the email date), {subject}. The extension is kept automatically."
              >
                <input
                  type="text"
                  className="cp-input cp-mono"
                  value={emailConfig.linkDownload.filename}
                  onChange={(e) => updateConfig({ linkDownload: { ...emailConfig.linkDownload, filename: e.target.value } })}
                  placeholder="{original} {date}  →  Taxes and Fees 2026-08-18.xlsx"
                />
              </EtlField>
            </EtlGrid>
            <div style={{ marginTop: 12 }}>
              <EtlBanner tone="info">
                Matching still works exactly as configured above: with subject matching the link is only
                followed when the subject matches; with filename matching the keyword is checked against the
                downloaded file's name. The file then runs through the normal parser.
              </EtlBanner>
            </div>
          </div>
        ) : null}
      </EtlSection>

      {allKeywords.length > 0 ? (
        <EtlSection
          title="What this would match"
          description={emailConfig.matchMode === 'exact' ? 'Exact mode — the whole name must equal a keyword.' : undefined}
          actions={<button type="button" className="cp-btn cp-sm" onClick={validateConfiguration}>Validate</button>}
        >
          <ul className="ep-match">
            {previewExamples.map((example, index) => (
              <li key={index} className={example.matches ? 'ep-hit' : undefined}>
                {example.matches ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
                <span className="ep-match-text">{example.text}</span>
                <span className={`cp-tag${example.matches ? ' cp-ok' : ''}`}>
                  {example.matches ? 'Accepted' : 'Skipped'}
                </span>
              </li>
            ))}
          </ul>
        </EtlSection>
      ) : null}

      {validationResult ? (
        <div style={{ marginTop: 16 }}>
          <EtlBanner
            tone={validationResult.isValid ? 'ok' : 'error'}
            title={validationResult.isValid ? 'Configuration is valid' : 'Configuration issues'}
          >
            {validationResult.errors.length || validationResult.warnings.length ? (
              <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                {validationResult.errors.map((err, i) => <li key={`e${i}`}>{err}</li>)}
                {validationResult.warnings.map((w, i) => <li key={`w${i}`}>{w}</li>)}
              </ul>
            ) : 'Every inbound file will be checked against these rules.'}
          </EtlBanner>
        </div>
      ) : null}

      <EtlSection title="Summary">
        <div className="ep-panel">
          <EtlKv
            rows={[
              { label: 'Type', value: emailConfig.fileType === 'custom' ? (emailConfig.customFileType || '—') : emailConfig.fileType, mono: true },
              { label: 'Checks', value: emailConfig.emailMatchType === 'file' ? 'Attachment filename' : 'Email subject' },
              { label: 'Keyword', value: emailConfig.auditFile || 'Not set', mono: !!emailConfig.auditFile },
              { label: 'Or', value: emailConfig.keywords.join(', '), mono: true },
              { label: 'Never', value: emailConfig.excludeKeywords.join(', '), mono: true },
              { label: 'Mode', value: emailConfig.matchMode === 'exact' ? 'Exact' : 'Contains' },
              { label: 'Formats', value: emailConfig.allowedExtensions.join(', ') || 'Any supported', mono: true },
              {
                label: 'Source',
                value: emailConfig.linkDownload.enabled
                  ? `Link in email (${LINK_PROVIDERS.find((p) => p.value === emailConfig.linkDownload.provider)?.label || emailConfig.linkDownload.provider})`
                  : 'Attachment',
              },
            ]}
          />
        </div>
      </EtlSection>
    </>
  );
};

export default EmailIntegrationConfig;
